import { useState, useEffect } from 'react';
import { api } from '../lib/api';

export default function AgencyFinance() {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadFinanceData = () => {
    setLoading(true);
    Promise.all([
      api.get('/api/agency-crm/invoices').catch(() => []),
      api.get('/api/client-module/clients').catch(() => []),
    ])
      .then(([invoicesRes, clientsRes]) => {
        setInvoices(invoicesRes || []);
        setClients(clientsRes || []);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadFinanceData();
  }, []);

  const handleAddInvoice = async (e) => {
    e.preventDefault();
    setSaving(true);
    const form = e.target;
    const body = {
      clientName: form.clientName.value,
      amount: Number(form.amount.value) || 0,
      status: 'Pending',
      dueDate: form.dueDate.value || new Date().toISOString().split('T')[0]
    };

    try {
      await api.post('/api/agency-crm/invoices', body);
      setShowAddForm(false);
      loadFinanceData();
    } catch (err) {
      alert('Failed to save invoice: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (invId, newStatus) => {
    const paidDate = newStatus === 'Paid' ? new Date().toISOString().split('T')[0] : '';
    try {
      await api.put(`/api/agency-crm/invoices/${invId}`, { status: newStatus, paidDate });
      setInvoices(prev => prev.map(iv => iv.id === invId ? { ...iv, status: newStatus, paidDate } : iv));
    } catch (err) {
      alert('Failed to update invoice: ' + err.message);
    }
  };

  const handleDeleteInvoice = async (id) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await api.del(`/api/agency-crm/invoices/${id}`);
      loadFinanceData();
    } catch (err) {
      alert('Failed to delete invoice: ' + err.message);
    }
  };

  if (loading && invoices.length === 0) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>Loading CRM Invoices Directory…</div>;
  }

  // Calculate aggregates
  const totalRevenue = invoices.filter(iv => iv.status === 'Paid').reduce((sum, iv) => sum + (iv.amount || 0), 0);
  const totalPending = invoices.filter(iv => iv.status === 'Pending').reduce((sum, iv) => sum + (iv.amount || 0), 0);

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif' }}>
      {/* 2 Top Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 30 }}>
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 16, padding: 22 }}>
          <div style={{ fontSize: '0.8rem', color: '#16a34a', fontWeight: 800, textTransform: 'uppercase' }}>Total Revenue Collected</div>
          <div style={{ fontSize: '2rem', fontWeight: 900, color: '#16a34a', marginTop: 6 }}>₹{totalRevenue.toLocaleString()}</div>
        </div>
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 16, padding: 22 }}>
          <div style={{ fontSize: '0.8rem', color: '#d97706', fontWeight: 800, textTransform: 'uppercase' }}>Total Receivables Pending</div>
          <div style={{ fontSize: '2rem', fontWeight: 900, color: '#d97706', marginTop: 6 }}>₹{totalPending.toLocaleString()}</div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 900, fontSize: '1.4rem' }}>CRM Invoices & Accounts</h2>
          <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#64748b' }}>Generate client billing records and track payment receivables</p>
        </div>
        <button onClick={() => setShowAddForm(prev => !prev)} className="cm-btn-emerald">
          {showAddForm ? 'Cancel' : '+ Generate Invoice'}
        </button>
      </div>

      {showAddForm && (
        <form onSubmit={handleAddInvoice} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 24, marginBottom: 24, maxWidth: 580 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 800 }}>Create New Invoice</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 12 }}>
            <div className="cm-field-group">
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: 4 }}>Client *</label>
              <select name="clientName" required style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <option value="">— Pick client —</option>
                {clients.map(c => <option key={c.id} value={c.clientName}>{c.clientName}</option>)}
              </select>
            </div>
            <div className="cm-field-group">
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: 4 }}>Invoice Amount (₹) *</label>
              <input name="amount" type="number" required placeholder="e.g. 25000" style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0' }} />
            </div>
          </div>
          <div className="cm-field-group" style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: 4 }}>Due Date *</label>
            <input name="dueDate" type="date" required defaultValue={new Date().toISOString().split('T')[0]} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0' }} />
          </div>

          <button type="submit" className="cm-btn-emerald" disabled={saving}>
            {saving ? 'Creating…' : 'Generate Invoice'}
          </button>
        </form>
      )}

      {/* Invoices List Table */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, overflow: 'hidden' }}>
        <table className="cm-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
              <th style={{ padding: '14px 18px' }}>Invoice ID</th>
              <th style={{ padding: '14px 18px' }}>Client Name</th>
              <th style={{ padding: '14px 18px' }}>Amount (₹)</th>
              <th style={{ padding: '14px 18px' }}>Status</th>
              <th style={{ padding: '14px 18px' }}>Due Date</th>
              <th style={{ padding: '14px 18px' }}>Paid Date</th>
              <th style={{ padding: '14px 18px', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map(iv => (
              <tr key={iv.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '14px 18px', fontWeight: 800 }}>#{iv.id.substring(0, 10)}</td>
                <td style={{ padding: '14px 18px', fontWeight: 700 }}>{iv.clientName}</td>
                <td style={{ padding: '14px 18px', fontWeight: 900 }}>₹{iv.amount.toLocaleString()}</td>
                <td style={{ padding: '14px 18px' }}>
                  <select
                    value={iv.status}
                    onChange={(e) => handleStatusChange(iv.id, e.target.value)}
                    style={{
                      background: iv.status === 'Paid' ? '#dcfce7' : '#fee2e2',
                      color: iv.status === 'Paid' ? '#16a34a' : '#dc2626',
                      border: 'none', padding: '4px 10px', borderRadius: 20, fontSize: '0.78rem', fontWeight: 800, cursor: 'pointer'
                    }}
                  >
                    <option value="Pending">Pending</option>
                    <option value="Paid">Paid</option>
                  </select>
                </td>
                <td style={{ padding: '14px 18px' }}>{iv.dueDate || '—'}</td>
                <td style={{ padding: '14px 18px', color: '#16a34a', fontWeight: 700 }}>{iv.paidDate || '—'}</td>
                <td style={{ padding: '14px 18px', textAlign: 'right' }}>
                  <button
                    onClick={() => handleDeleteInvoice(iv.id)}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1rem' }}
                    title="Delete Record"
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
